import { CommonModule } from '@angular/common';
import { Component, ChangeDetectionStrategy, Input, EventEmitter, Output } from '@angular/core';

import { isEmpty } from '@app/util/util';
import {
  TypographyColor, TypographyFontFamily, TypographyFontSize
} from '@design-system/components/atoms/typography/entities/typography.types';
import {
  ChipFilterActionType, ChipFilterOption, ChipFilterPriority, ChipFilterSize, ChipFilterState
} from '@design-system/components/molecules/chip-filter/entities/chip-filter.types';
import { Icon, IconFillColor } from '@design-system/components/atoms/icon/entities/icon.types';
import { DividerOrientation } from '@design-system/components/atoms/divider/entities/divider.types';
import { TypographyDsComponent } from '@design-system/components/atoms/typography/typography.component';
import { IconDsComponent } from '@design-system/components/atoms/icon/icon.component';
import { DividerDsComponent } from '@design-system/components/atoms/divider/divider.component';

@Component({
  selector: 'app-ds-chip-filter',
  templateUrl: './chip-filter.component.html',
  styleUrls: ['./chip-filter.component.sass'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    TypographyDsComponent,
    IconDsComponent,
    DividerDsComponent,
  ]
})
export class ChipFilterDsComponent {

  @Input() size = ChipFilterSize.XS;
  @Input() priority = ChipFilterPriority.Primary;
  @Input() actionType = ChipFilterActionType.Tag;
  @Input() state = ChipFilterState.Default;
  @Input() options: ChipFilterOption[] = [];

  @Output() clickEvent: EventEmitter<ChipFilterOption> = new EventEmitter<ChipFilterOption>();

  public readonly fontFamily = TypographyFontFamily;
  public readonly dividerOrientation = DividerOrientation.Vertical;

  constructor() { }

  get items(): ChipFilterOption[] {
    return this.options.filter(item => item);
  }

  get slice(): number {
    return this.priority === ChipFilterPriority.Primary ? 1 : this.items.length;
  }

  get action(): Icon {
    if (this.priority === ChipFilterPriority.Primary && this.actionType !== ChipFilterActionType.Tag && !this.withoutText) {
      return this.actionType === ChipFilterActionType.Dropdown ? Icon.ChevronFilledDown : Icon.Close;
    } else {
      return null;
    }
  }

  get iconSize(): number {
    switch (true) {
      case this.size === ChipFilterSize.SM && this.priority === ChipFilterPriority.Primary:
        return 16;
      case this.size === ChipFilterSize.RG && this.priority === ChipFilterPriority.Primary:
        return 20;
      default:
        return 14;
    }
  }

  get fontSize(): TypographyFontSize {
    switch (true) {
      case this.size === ChipFilterSize.XS && this.priority === ChipFilterPriority.Primary:
        return TypographyFontSize.XXXS;
      case this.size === ChipFilterSize.SM && this.priority === ChipFilterPriority.Primary:
      case this.priority === ChipFilterPriority.Secondary:
        return TypographyFontSize.XXS;
      default:
        return TypographyFontSize.XS;
    }
  }

  get actionSize(): number {
    switch (this.size) {
      case ChipFilterSize.SM:
        return this.action === Icon.ChevronFilledDown ? 24 : 16;
      case ChipFilterSize.RG:
        return this.action === Icon.ChevronFilledDown ? 30 : 24;
      default:
        return this.action === Icon.ChevronFilledDown ? 24 : 13;
    }
  }

  get fontColor(): TypographyColor {
    if (this.state === ChipFilterState.Disabled) {
      return TypographyColor.Disabled;
    } else {
      return TypographyColor.Primary;
    }
  }

  get actionFillColor(): IconFillColor {
    return this.state === ChipFilterState.Disabled ? IconFillColor.Disabled : IconFillColor.Primary;
  }

  get withoutText(): boolean {
    return this.priority === ChipFilterPriority.Primary && isEmpty(this.items?.[0]?.Label);
  }

  public iconFillColor(isSpecial: boolean): IconFillColor {
    if (this.state === ChipFilterState.Disabled) {
      return IconFillColor.Disabled;
    } else {
      return isSpecial ? IconFillColor.Special : IconFillColor.Primary;
    }
  }

  public trackById(index: number): number {
    return index;
  }

  public clickEventTrigger(option: ChipFilterOption): void {
    if (this.state !== ChipFilterState.Disabled) {
      this.clickEvent.emit(option);
    }
  }

}
